/**
 * This file exists to avoid circular dependencies due to the `APP_STORE` import
 * If we can remove APP_STORE we can merge this code back into `base.ts`
 **/
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import axios, { AxiosRequestConfig, AxiosError, isCancel } from 'axios';
import Cookies from 'js-cookie';
import { APP_STORE } from '../../store/store';
import { SUBS_SCHEMA } from '../../../config/auth';
import { recoverFromLookerAuthError, refreshLookerAuthToken } from './lookerTokenRefresh';
// import { signOut } from '../authActions';

export function objectToQueryString(obj) {
  const params = new URLSearchParams();

  for (const key in obj) {
    // see https://eslint.org/docs/latest/rules/no-prototype-builtins
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      params.append(key, obj[key]);
    }
  }

  return params.toString();
}
// NOTE - Token Renewal logic is not added here as no call is being made to looker through these hooks as of today
// avoid circular dependencies by lazily creating the axios instance
// Exported for callers that need the whole response (status and headers) of a
// failed request, which the base query below reduces to { status, data }.
export const createAxiosInstance = () => {
  const apiAxiosWithAuth = axios.create({
    withCredentials: true,
    headers: {
      'Content-type': 'application/json',
    },
  });

  apiAxiosWithAuth.interceptors.request.use(async (config) => {
    if (!config.headers) {
      config.headers = new axios.AxiosHeaders();
    }

    const isLookerAuthReplay = Boolean(
      (config as typeof config & { __lookerAuthRetried?: boolean }).__lookerAuthRetried
    );
    const replayToken = config.headers['auth_token'];
    let looker_auth_token =
      isLookerAuthReplay && typeof replayToken === 'string' ? replayToken : Cookies.get('user_auth_token') || '';

    // Check server expiration time instead of just cookie existence
    const serverExpirationTime = localStorage.getItem('user_auth_token_expiration');
    const isExpiredOnServer = !serverExpirationTime || Date.now() + 300000 >= parseInt(serverExpirationTime); // 5 min buffer

    if (!isLookerAuthReplay && (!looker_auth_token || looker_auth_token === 'undefined' || isExpiredOnServer)) {
      looker_auth_token = await refreshLookerAuthToken();
    }

    config.headers['auth_token'] = looker_auth_token;

    // AUTH0_TODO Q2022-3777 - Temporary Solution to send sub token to BE, this token should be added in user session
    const subAuthToken = localStorage.getItem('subscriber-auth-token');
    if (subAuthToken) {
      config.headers['subscriber-auth-token'] = subAuthToken;
    }

    const store = APP_STORE;
    if (store.getState().root.customerInfo && store.getState().root.customerInfo.length && subAuthToken) {
      const custInfo = store.getState().root.customerInfo.find((i) => i.cust_code === SUBS_SCHEMA);
      if (custInfo?.additional_user_info?.isDemoUser && custInfo?.additional_user_info?.replaceKeywords) {
        config.headers['isdemouser'] = true;
        config.headers['sandbox-env-replacements'] = btoa(
          JSON.stringify(custInfo?.additional_user_info?.replaceKeywords)
        );
      }
    } else {
      config.headers['checkforsandboxenv'] = true;
    }

    // TODO check after Q2022-3235 is in production, if this `if` statement can be removed:
    if (config.url?.startsWith(process.env.NEXT_PUBLIC_QUATTR_API_URL as string)) {
      let queryString = '';
      if (config.params) {
        queryString = objectToQueryString(config.params);
        config.params = {};
      }
      config.url = `${process.env.NEXT_PUBLIC_HOST_URL}/api/qwa-backend?url=${encodeURIComponent(
        `${config.url}${queryString ? `?${queryString}` : ``}`
      )}`;
    }

    config.headers['Content-Type'] = 'application/json';
    return config;
  });

  apiAxiosWithAuth.interceptors.response.use(undefined, async (error) => {
    if (error.response?.status === 401) {
      try {
        const { signOut } = await import('../authActions');
        return recoverFromLookerAuthError(
          error,
          (config) => apiAxiosWithAuth.request(config as AxiosRequestConfig),
          () => signOut()
        );
      } catch (signOutError) {
        return Promise.reject(signOutError);
      }
    }
    return Promise.reject(error);
  });

  return apiAxiosWithAuth;
};

type AxiosRequestArgs = {
  url: string;
  method: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  signal?: AbortSignal;
};

// see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#axios-basequery
export const axiosBaseQueryWithAuth = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' }
): BaseQueryFn<AxiosRequestArgs, unknown, unknown> => {
  const apiAxiosWithAuth = createAxiosInstance();

  return async ({ url, method, data, params }, { signal }) => {
    try {
      const result = await apiAxiosWithAuth({
        url: baseUrl + url,
        method,
        data,
        params,
        signal,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      if (axios.isAxiosError(axiosError)) {
        if (axios.isCancel(axiosError)) {
          console.warn('Axios request cancelled', axiosError.message);
          return {
            error: {
              status: 'ABORTED',
              data: null,
            },
          };
        }

        return {
          error: {
            status: err.response?.status || 500,
            data: err.response?.data || err.message,
          },
        };
      }

      return {
        error: {
          status: 500,
          data: 'Unknown error occurred',
        },
      };
    }
  };
};
